// TelcoTrack — physical D5 sheets. Renderer follows the cabinet (MUK vs RAY-TR3), not SDF/RSU.

import { FIBER_COLOR_LABELS_CS, fiberNumberToColors } from './fiber-color-code.constants';
import { getRozvadecCassetteType } from './rozvadec-cassette.constants';
import { getRayOfsmModule, RAY_OFSM_COLUMN_SIZE } from './rozvadec-ofsm.constants';
import { compactRayD5Destination, formatRaySplitterStandLabel } from './rozvadec-occupancy-schema.raytr3-grid';
import { occupancyKm5ConnectorPrintLabel } from './rozvadec-occupancy-schema.ids';
import type {
  RozvadecNetworkRole,
  RozvadecOccupancyCassette,
  RozvadecOccupancyCassetteBlock,
  RozvadecOccupancyDoorSplitter,
  RozvadecOccupancyGenerateInput,
  RozvadecOccupancyPortCell,
  RozvadecOccupancyPositionSlot,
  RozvadecOccupancySchema,
  RozvadecOccupancySeatStatus,
  RozvadecOccupancySheet,
  RozvadecOccupancySplitter,
} from './rozvadec-occupancy-schema.types';

/** RAY-TR3 D5 template: eight 3U bays, top → bottom. Numbering comes from the inserted module. */
export const RAY_TR3_D5_SLOT_COUNT = 8;
export const RAY_TR3_D5_SLOT_U = 3;

const EMPTY = '—';

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function hopPort(label: string): string {
  const sc = /^SC-(\d+)$/.exec(label);
  if (sc) return `SC-${pad2(Number(sc[1]))}`;
  const lc = /^LC-(\d+)([ab])$/.exec(label);
  if (lc) return `LC-${pad2(Number(lc[1]))}${lc[2]}`;
  const out = /^OUT\s+\d+\/(\d+)$/.exec(label);
  if (out) return `OUT-${pad2(Number(out[1]))}`;
  const inn = /^IN\s+\d+\/\d+$/.exec(label);
  if (inn) return 'IN';
  return label;
}

function fiberColorLabel(physical: number | null | undefined): string | null {
  if (physical == null || physical < 1) return null;
  const result = fiberNumberToColors(physical, 'DEFAULT', 'OUTER');
  if (!result.ok) return null;
  const label = FIBER_COLOR_LABELS_CS[result.value.fiberColor];
  return result.value.stripe ? `${label} (proužek)` : label;
}

export function inferRozvadecNetworkRole(
  input: RozvadecOccupancyGenerateInput,
  typeId: string | null | undefined,
): RozvadecNetworkRole {
  if (input.networkRole === 'SDF' || input.networkRole === 'aSDF' || input.networkRole === 'none') {
    return input.networkRole;
  }
  const name = `${input.objectName ?? ''} ${input.cabinetPrintName ?? ''}`;
  if (/\baSDF\d*/i.test(name)) return 'aSDF';
  if (/\bSDF\d*/i.test(name)) return 'SDF';
  void typeId;
  return 'none';
}

export function localityFromOccupancyInput(
  input: RozvadecOccupancyGenerateInput,
  networkRole: RozvadecNetworkRole,
): string {
  if ((input.locality ?? '').trim()) return input.locality!.trim();
  const name = (input.objectName ?? input.cabinetPrintName ?? '').trim();
  if (!name) return EMPTY;
  const stripped = name.replace(/\ba?SDF\s*\d*/gi, '').replace(/\s{2,}/g, ' ').trim();
  return stripped || name;
}

function buildingLabel(
  buildingId: string | null | undefined,
  input: RozvadecOccupancyGenerateInput,
): string {
  if (!buildingId) return EMPTY;
  const labeled = (input.buildingLabels ?? []).find((item) => item.buildingId === buildingId);
  if (labeled?.label.trim()) return labeled.label.trim();
  const row = (input.buildings ?? []).find((item) => item.buildingId === buildingId);
  if (row?.label?.trim()) return row.label.trim();
  return buildingId;
}

function cableMeta(
  cableId: string | null | undefined,
  input: RozvadecOccupancyGenerateInput,
): { typOk: string | null; destName: string | null; sourceName: string | null } {
  const incoming = (input.incomingCables ?? []).find((item) => item.cableId === cableId);
  return {
    typOk: incoming?.typOk ?? null,
    destName: incoming?.destRozvadecName ?? null,
    sourceName: incoming?.sourceRozvadecName ?? null,
  };
}

type HopIndex = {
  connectorToSplitter: Map<string, string>;
  splitterOutToConnector: Map<string, string>;
  connectorToBuilding: Map<string, string>;
  splitterOutToBuilding: Map<string, string>;
};

function buildHopIndex(schema: RozvadecOccupancySchema, input: RozvadecOccupancyGenerateInput): HopIndex {
  const connectorToSplitter = new Map<string, string>();
  const splitterOutToConnector = new Map<string, string>();
  const connectorToBuilding = new Map<string, string>();
  const splitterOutToBuilding = new Map<string, string>();

  const incomingKey = (cableId: string | null | undefined, fiber: number | null | undefined): string =>
    `${(cableId ?? '').trim()}#${fiber ?? 0}`;

  const connectorsByIncoming = new Map<string, string>();
  for (const connector of schema.graph.connectors) {
    if (connector.incoming?.cableId) {
      connectorsByIncoming.set(
        incomingKey(connector.incoming.cableId, connector.incoming.physicalFiber),
        connector.printLabel,
      );
    }
  }

  for (const splitter of schema.graph.splitters) {
    for (const port of splitter.inputs) {
      if (!port.incoming?.cableId) continue;
      const sc = connectorsByIncoming.get(incomingKey(port.incoming.cableId, port.incoming.physicalFiber));
      if (sc) {
        connectorToSplitter.set(sc, `${splitter.printLabel} IN`);
      }
    }
    for (const port of splitter.outputs) {
      const assignment = schema.graph.assignments.find((item) => item.splitterPortId === port.id);
      const buildingId = assignment?.buildingId ?? port.target?.buildingId ?? null;
      if (buildingId) {
        splitterOutToBuilding.set(port.id, buildingId);
        const lc = schema.graph.assignments.find(
          (item) =>
            item.via === 'connector' &&
            item.buildingId === buildingId &&
            (assignment ? item.cableId === assignment.cableId : true),
        );
        const connector = lc
          ? schema.graph.connectors.find((item) => item.id === lc.connectorId)
          : null;
        if (connector) {
          splitterOutToConnector.set(port.id, connector.printLabel);
        }
      }
    }
  }

  for (const assignment of schema.graph.assignments) {
    if (assignment.via !== 'connector' || !assignment.connectorId) continue;
    const connector = schema.graph.connectors.find((item) => item.id === assignment.connectorId);
    if (connector) connectorToBuilding.set(connector.printLabel, assignment.buildingId);
  }

  void input;
  return { connectorToSplitter, splitterOutToConnector, connectorToBuilding, splitterOutToBuilding };
}

function formatHop(from: string, to: string): string {
  return `${hopPort(from)} → ${to}`;
}

function portStatus(occupied: boolean, reserved: boolean, planned: boolean): RozvadecOccupancySeatStatus {
  if (reserved && !occupied) return 'rez.';
  if (occupied) return 'obsazeno';
  if (planned) return 'plán';
  if (reserved) return 'rez.';
  return 'volno';
}

function cassetteKind(cassetteType: string | null | undefined): RozvadecOccupancyCassetteBlock['kind'] {
  if (cassetteType === 'KM5_SC') return 'KM5_SC';
  if (cassetteType === 'KM5_LC') return 'KM5_LC';
  if (cassetteType === 'KM4') return 'KM4';
  if (cassetteType === 'KM6') return 'KM6';
  if ((cassetteType ?? '').startsWith('KM5_SPLITTER')) return 'KM5_SPLITTER';
  return 'empty';
}

function routeForCassette(
  cassette: RozvadecOccupancyCassette,
  schema: RozvadecOccupancySchema,
  input: RozvadecOccupancyGenerateInput,
): string {
  const parts: string[] = [cassette.printLabel];
  const connector = schema.graph.connectors.find((item) => item.cassetteId === cassette.id && item.incoming);
  const splice = schema.graph.splices.find((item) => item.cassetteId === cassette.id && item.fibers[0]);
  const cableId = connector?.incoming?.cableId ?? splice?.fibers[0]?.cableId ?? null;
  if (cableId) {
    const meta = cableMeta(cableId, input);
    parts.push(cableId);
    if (meta.typOk) parts.push(meta.typOk);
    if (meta.sourceName) parts.push(meta.sourceName);
    if (meta.destName) parts.push(meta.destName);
  }
  if (input.projectLabel?.trim()) parts.push(input.projectLabel.trim());
  return parts.join(' · ');
}

function km5Ports(
  cassette: RozvadecOccupancyCassette,
  schema: RozvadecOccupancySchema,
  input: RozvadecOccupancyGenerateInput,
  hops: HopIndex,
): RozvadecOccupancyPortCell[] {
  const family = cassette.adapterFamily === 'LC' ? 'LC' : 'SC';
  const count = family === 'LC' ? 24 : 12;
  const reserveFibers = new Set(
    schema.graph.fibers.filter((item) => item.role === 'reserve').map((item) => `${item.cableId}#${item.physicalFiber}`),
  );
  const ports: RozvadecOccupancyPortCell[] = [];
  for (let ordinal = 1; ordinal <= count; ordinal += 1) {
    const printLabel = occupancyKm5ConnectorPrintLabel(family, ordinal);
    const connector = schema.graph.connectors.find(
      (item) => item.cassetteId === cassette.id && item.printLabel === printLabel,
    );
    const assignment = connector
      ? schema.graph.assignments.find((item) => item.connectorId === connector.id)
      : undefined;
    const incoming = connector?.incoming ?? null;
    const reserved = incoming
      ? reserveFibers.has(`${incoming.cableId}#${incoming.physicalFiber}`)
      : false;
    const hopToSpl = hops.connectorToSplitter.get(printLabel) ?? null;
    const hopToBuilding = hops.connectorToBuilding.get(printLabel) ?? assignment?.buildingId ?? null;
    let hopRef: string | null = null;
    let target = 'volno';
    if (hopToSpl) {
      hopRef = formatHop(printLabel, hopToSpl);
      target = hopToSpl;
    } else if (hopToBuilding) {
      const building = buildingLabel(hopToBuilding, input);
      hopRef = formatHop(printLabel, `objekt ${building}`);
      target = building;
    } else if (reserved) {
      target = 'rez.';
    }
    const occupied = !!(incoming || assignment || hopToSpl);
    ports.push({
      id: connector?.id ?? `${cassette.id}:${printLabel}`,
      printLabel,
      status: portStatus(occupied && !reserved, reserved, false),
      source: incoming
        ? incoming.physicalFiber > 0
          ? `${incoming.cableId} / ${incoming.physicalFiber}`
          : incoming.cableId
        : EMPTY,
      target,
      hopRef,
      colorLabel: fiberColorLabel(incoming?.physicalFiber ?? assignment?.physicalFiber),
      logicalFiber: assignment?.logicalFiber != null ? String(assignment.logicalFiber) : null,
    });
  }
  return ports;
}

function km4Ports(
  cassette: RozvadecOccupancyCassette,
  schema: RozvadecOccupancySchema,
  input: RozvadecOccupancyGenerateInput,
): RozvadecOccupancyPortCell[] {
  const reserveFibers = new Set(
    schema.graph.fibers.filter((item) => item.role === 'reserve').map((item) => `${item.cableId}#${item.physicalFiber}`),
  );
  const ports: RozvadecOccupancyPortCell[] = [];
  for (let seat = 1; seat <= 24; seat += 1) {
    const splice = schema.graph.splices.find(
      (item) => item.cassetteId === cassette.id && item.kind === 'tray' && item.seat === seat,
    );
    const assignment = splice
      ? schema.graph.assignments.find((item) => item.spliceId === splice.id)
      : undefined;
    const left = splice?.fibers[0] ?? null;
    const right = splice?.fibers[1] ?? null;
    const reserved =
      (left && reserveFibers.has(`${left.cableId}#${left.physicalFiber}`)) ||
      (right && reserveFibers.has(`${right.cableId}#${right.physicalFiber}`));
    const leftLabel = left
      ? left.physicalFiber > 0
        ? `${left.cableId} / ${left.physicalFiber}`
        : left.cableId
      : EMPTY;
    let target = 'volno';
    if (assignment) {
      target = buildingLabel(assignment.buildingId, input);
    } else if (right) {
      target = right.physicalFiber > 0 ? `${right.cableId} / ${right.physicalFiber}` : right.cableId;
    } else if (reserved) {
      target = 'rez.';
    }
    const occupied = !!(left || right || assignment);
    ports.push({
      id: splice?.id ?? `${cassette.id}:S${pad2(seat)}`,
      printLabel: String(seat),
      status: portStatus(occupied && !reserved, !!reserved, false),
      source: leftLabel,
      target,
      hopRef: assignment ? `${cassette.printLabel} / svar ${seat} → objekt ${target}` : null,
      colorLabel: fiberColorLabel(left?.physicalFiber ?? right?.physicalFiber),
      logicalFiber: assignment?.logicalFiber != null ? String(assignment.logicalFiber) : null,
    });
  }
  return ports;
}

function splitterCassettePorts(
  cassette: RozvadecOccupancyCassette,
  schema: RozvadecOccupancySchema,
  input: RozvadecOccupancyGenerateInput,
  hops: HopIndex,
): RozvadecOccupancyPortCell[] {
  const splitters = schema.graph.splitters.filter((item) => item.cassetteId === cassette.id);
  const ports: RozvadecOccupancyPortCell[] = [];
  for (const splitter of splitters) {
    for (const port of splitter.inputs) {
      const incoming = port.incoming;
      ports.push({
        id: port.id,
        printLabel: `${splitter.printLabel} IN`,
        status: incoming ? 'obsazeno' : 'volno',
        source: incoming
          ? incoming.physicalFiber > 0
            ? `${incoming.cableId} / ${incoming.physicalFiber}`
            : incoming.cableId
          : EMPTY,
        target: incoming ? `${splitter.printLabel} IN` : 'volno',
        hopRef: incoming ? `${splitter.printLabel} IN` : null,
        colorLabel: fiberColorLabel(incoming?.physicalFiber),
        logicalFiber: null,
      });
    }
    for (const port of splitter.outputs) {
      const assignment = schema.graph.assignments.find((item) => item.splitterPortId === port.id);
      const lc = hops.splitterOutToConnector.get(port.id);
      const buildingId = hops.splitterOutToBuilding.get(port.id) ?? assignment?.buildingId ?? port.target?.buildingId;
      const outLabel = `${splitter.printLabel} ${hopPort(port.printLabel)}`;
      let target = 'volno';
      let hopRef: string | null = null;
      if (lc) {
        target = lc;
        hopRef = `${outLabel} → ${hopPort(lc)}`;
      } else if (buildingId) {
        const building = buildingLabel(buildingId, input);
        target = building;
        hopRef = `${outLabel} → objekt ${building}`;
      }
      ports.push({
        id: port.id,
        printLabel: hopPort(port.printLabel),
        status: portStatus(!!(assignment || port.target || port.incoming), false, false),
        source: port.incoming
          ? `${port.incoming.cableId} / ${port.incoming.physicalFiber}`
          : EMPTY,
        target,
        hopRef,
        colorLabel: fiberColorLabel(port.incoming?.physicalFiber ?? assignment?.physicalFiber),
        logicalFiber: assignment?.logicalFiber != null ? String(assignment.logicalFiber) : null,
      });
    }
  }
  return ports;
}

function cassetteBlock(
  cassette: RozvadecOccupancyCassette,
  schema: RozvadecOccupancySchema,
  input: RozvadecOccupancyGenerateInput,
  hops: HopIndex,
): RozvadecOccupancyCassetteBlock {
  const kind = cassetteKind(cassette.cassetteType);
  let ports: RozvadecOccupancyPortCell[] = [];
  if (kind === 'KM5_SC' || kind === 'KM5_LC') {
    ports = km5Ports(cassette, schema, input, hops);
  } else if (kind === 'KM4' || kind === 'KM6') {
    ports = km4Ports(cassette, schema, input);
  } else if (kind === 'KM5_SPLITTER') {
    ports = splitterCassettePorts(cassette, schema, input, hops);
  }
  return {
    cassetteId: cassette.id,
    cassetteType: cassette.cassetteType,
    printLabel: cassette.printLabel,
    startPosition: cassette.startPosition,
    span: getRozvadecCassetteType(cassette.cassetteType)?.span ?? 1,
    kind,
    ports,
    routeLabel: routeForCassette(cassette, schema, input),
  };
}

function doorSplitters(
  schema: RozvadecOccupancySchema,
  input: RozvadecOccupancyGenerateInput,
  hops: HopIndex,
): RozvadecOccupancyDoorSplitter[] {
  const loose = schema.graph.splitters.filter(
    (item) => !item.insideCassette || item.ratio === '1:32',
  );
  return loose.map((splitter) => doorSplitterBlock(splitter, schema, input, hops));
}

function doorSplitterBlock(
  splitter: RozvadecOccupancySplitter,
  schema: RozvadecOccupancySchema,
  input: RozvadecOccupancyGenerateInput,
  hops: HopIndex,
): RozvadecOccupancyDoorSplitter {
  const inn = splitter.inputs[0];
  const incoming = inn?.incoming ?? null;
  const sc = incoming
    ? schema.graph.connectors.find(
        (item) =>
          item.incoming?.cableId === incoming.cableId &&
          item.incoming.physicalFiber === incoming.physicalFiber,
      )
    : null;
  const inputCell: RozvadecOccupancyPortCell = {
    id: inn?.id ?? `${splitter.id}:in`,
    printLabel: `${splitter.printLabel} IN`,
    status: incoming ? 'obsazeno' : 'volno',
    source: incoming
      ? `${incoming.cableId} / ${incoming.physicalFiber}`
      : EMPTY,
    target: incoming ? `${splitter.printLabel} IN` : 'volno',
    hopRef: sc ? formatHop(sc.printLabel, `${splitter.printLabel} IN`) : null,
    colorLabel: fiberColorLabel(incoming?.physicalFiber),
    logicalFiber: null,
  };
  const outputs = splitter.outputs.map((port) => {
    const assignment = schema.graph.assignments.find((item) => item.splitterPortId === port.id);
    const lc = hops.splitterOutToConnector.get(port.id);
    const buildingId = hops.splitterOutToBuilding.get(port.id) ?? assignment?.buildingId ?? port.target?.buildingId;
    const outLabel = `${splitter.printLabel} ${hopPort(port.printLabel)}`;
    let target = 'volno';
    let hopRef: string | null = null;
    if (lc) {
      target = hopPort(lc);
      hopRef = `${outLabel} → ${hopPort(lc)}`;
    } else if (buildingId) {
      target = buildingLabel(buildingId, input);
      hopRef = `${outLabel} → objekt ${target}`;
    }
    return {
      id: port.id,
      printLabel: hopPort(port.printLabel),
      status: portStatus(!!(assignment || port.target), false, false),
      source: EMPTY,
      target,
      hopRef,
      colorLabel: fiberColorLabel(assignment?.physicalFiber),
      logicalFiber: assignment?.logicalFiber != null ? String(assignment.logicalFiber) : null,
    };
  });
  return {
    id: splitter.id,
    printLabel: splitter.printLabel,
    ratio: splitter.ratio,
    input: inputCell,
    outputs,
  };
}

function collectReferences(
  positions: RozvadecOccupancyPositionSlot[],
  doors: RozvadecOccupancyDoorSplitter[],
): string[] {
  const refs = new Set<string>();
  for (const slot of positions) {
    for (const port of slot.cassette?.ports ?? []) {
      if (port.hopRef) refs.add(port.hopRef);
    }
  }
  for (const door of doors) {
    if (door.input.hopRef) refs.add(door.input.hopRef);
    for (const out of door.outputs) {
      if (out.hopRef) refs.add(out.hopRef);
    }
  }
  return [...refs];
}

function mukNotes(schema: RozvadecOccupancySchema, networkRole: RozvadecNetworkRole): string[] {
  const notes: string[] = [
    'Fyzický pohled montéra. Číslování 1 nahoře, stejný směr na obrazovce i v exportu.',
    'Adresa: řada → pozice kazety → port/svar → cíl. Odkazy místo křížících čar.',
  ];
  if (networkRole === 'SDF') {
    notes.push(
      'Běžné SDF v MUK: přívod z aSDF vlevo odspodu (KM5 SC/APC); výstupy objektů vpravo odspodu (KM5 LC/APC); 1:32 ve dveřích; pokračování SDF = KM4; 1:4 nepoužívat.',
    );
  }
  if (networkRole === 'aSDF') {
    notes.push(
      'aSDF: agregace 1:4/1:8 vlevo shora dolů; 1:32 ve dveřích; výstupy objekty LC vpravo odspodu; výstupy další SDF SC; dálkové v KM4.',
    );
  }
  if (schema.cabinetCode === 'MUK48') {
    notes.push('MUK48 = dva listy Řada I / Řada II (ne řada 1–48). KM4 = 1 pozice, KM5 = 2 sloučené. Splittery 1:32 ve dveřích.');
  }
  notes.push('MUK D5: A3 naležato. KM4 = 12×2 svary. KM5 = sloučený blok, obě čísla na levé stupnici.');
  return notes;
}

function mukRowPrintLabel(rowId: string, fallback: string, cabinetCode: string): string {
  if (cabinetCode === 'MUK48') {
    if (rowId === 'rada_1') return 'Řada I';
    if (rowId === 'rada_2') return 'Řada II';
  }
  return fallback || 'Řada';
}

function buildMukSheets(
  schema: RozvadecOccupancySchema,
  input: RozvadecOccupancyGenerateInput,
): RozvadecOccupancySheet[] {
  const hops = buildHopIndex(schema, input);
  const doors = doorSplitters(schema, input, hops);
  const configuration = schema.cabinetCode === 'MUK48' ? '2×24' : '1×24';
  const rows = schema.rows.length > 0 ? schema.rows : [{ id: 'rada_1', label: 'Řada', positions: 24, fillFrom: 'top' as const }];
  return rows.map((row, index) => {
    const placements = schema.graph.cassettes.filter((item) => item.rowId === row.id);
    const blocks = new Map<string, RozvadecOccupancyCassetteBlock>();
    for (const cassette of placements) {
      blocks.set(cassette.id, cassetteBlock(cassette, schema, input, hops));
    }
    const positions: RozvadecOccupancyPositionSlot[] = [];
    for (let position = 1; position <= 24; position += 1) {
      const cassette = placements.find((item) => item.occupiedPositions.includes(position));
      const start = cassette?.startPosition === position;
      const span = cassette ? getRozvadecCassetteType(cassette.cassetteType)?.span ?? 1 : 1;
      const scaleLabels = start && span === 2 ? [position, position + 1] : [position];
      positions.push({
        position,
        scaleLabels,
        empty: !cassette,
        isCassetteStart: !!start,
        render: !cassette || !!start,
        cassette: cassette ? blocks.get(cassette.id) ?? null : null,
      });
    }
    const references = collectReferences(positions, doors);
    return {
      id: `muk:${row.id}`,
      kind: 'muk-row',
      title: `${schema.printName} · ${row.label}`,
      rowId: row.id,
      rowLabel: mukRowPrintLabel(row.id, row.label, schema.cabinetCode),
      sideLabel: null,
      frontView: true,
      pageFormat: 'A3-landscape',
      sapCode: schema.sapCode ?? EMPTY,
      configuration,
      networkRole: schema.networkRole,
      locality: schema.locality ?? EMPTY,
      cabinetTypeLabel: schema.cabinetCode === 'MUK48' ? 'MUK48' : 'MUK24',
      printName: schema.printName,
      objectName: schema.objectName ?? schema.printName,
      statusLabel: 'přední pohled',
      positions,
      doorSplitters: doors,
      references,
      notes: mukNotes(schema, schema.networkRole),
      sheetIndex: index + 1,
      sheetCount: rows.length,
    };
  });
}

function isJumperModule(sap: string, label: string): boolean {
  return /jumper/i.test(`${sap} ${label}`);
}

function ofsmPatchDestination(
  schema: RozvadecOccupancySchema,
  moduleId: string,
  position: number,
): string | null {
  const patches = schema.rsuOccupancy?.patchConnections ?? [];
  for (const patch of patches) {
    const ends = [patch.source, patch.target];
    const self = ends.find(
      (port) => port.kind === 'ofsm-connector' && port.moduleId === moduleId && port.connectorPosition === position,
    );
    if (!self) continue;
    const peer = ends.find((port) => port !== self);
    if (!peer) continue;
    if (peer.kind === 'splitter-in') return `SPL ${peer.splitterIndex} IN`;
    if (peer.kind === 'splitter-out') return `SPL ${peer.splitterIndex} OUT ${peer.output}`;
    if (peer.kind === 'ofsm-connector') return `OFSM P${peer.connectorPosition}`;
    if (peer.kind === 'cable-fiber') {
      const name = peer.cableId.trim();
      return compactRayD5Destination(name.startsWith('OK') ? name : `OK ${name}`);
    }
  }
  return null;
}

function ofsmPortsForModule(
  schema: RozvadecOccupancySchema,
  moduleId: string,
  sap: string,
  odfOrder: number,
): RozvadecOccupancyPortCell[] {
  const def = getRayOfsmModule(sap);
  if (!def || def.kind !== 'patch-weld') return [];
  const terms = schema.rsuOccupancy?.terminations.filter((item) => item.moduleId === moduleId) ?? [];
  const byPos = new Map(terms.map((item) => [item.connectorPosition, item]));
  const ports: RozvadecOccupancyPortCell[] = [];
  for (let position = 1; position <= def.connectorCapacity; position += 1) {
    const term = byPos.get(position);
    const destRaw = term?.destinationLabel ?? term?.destinationObjectId ?? null;
    const patchDest = ofsmPatchDestination(schema, moduleId, position);
    const reserved = term?.status === 'reserve';
    const connected = term?.status === 'connected' || !!destRaw || !!patchDest;
    let status: RozvadecOccupancySeatStatus = 'rez.';
    let target = 'rezerva';
    if (reserved) {
      status = 'rez.';
      target = 'rezerva';
    } else if (connected) {
      status = 'obsazeno';
      target = compactRayD5Destination(destRaw ?? patchDest ?? 'obsazeno');
    }
    ports.push({
      id: term?.id ?? `${moduleId}:P${position}`,
      printLabel: String(position),
      status,
      source: term?.physicalFiberNumber != null ? String(term.physicalFiberNumber) : '',
      target,
      hopRef: destRaw || patchDest
        ? `ODF ${odfOrder} / ${position} → ${compactRayD5Destination(destRaw ?? patchDest)}`
        : null,
      colorLabel: fiberColorLabel(term?.physicalFiberNumber),
      logicalFiber: term?.logicalFiberNumber != null ? String(term.logicalFiberNumber) : null,
    });
  }
  return ports;
}

function splitterModulePorts(
  schema: RozvadecOccupancySchema,
  moduleId: string,
  sap: string,
  moduleIndex: number,
): RozvadecOccupancyPortCell[] {
  const def = getRayOfsmModule(sap);
  if (!def || def.kind !== 'splitter') return [];
  const outs = def.splitterRatio === '1:16' ? 16 : 8;
  const ports: RozvadecOccupancyPortCell[] = [];
  for (let index = 1; index <= def.splitterCount; index += 1) {
    ports.push({
      id: `${moduleId}:spl:${index}:in`,
      printLabel: `IN-${index}`,
      status: 'volno',
      source: EMPTY,
      target: `splitterový modul ${moduleIndex} / pozice ${index} / IN`,
      hopRef: `splitterový modul ${moduleIndex} / pozice ${index} / IN`,
      colorLabel: null,
      logicalFiber: null,
    });
    for (let out = 1; out <= outs; out += 1) {
      const patch = (schema.rsuOccupancy?.patchConnections ?? []).find((item) => {
        const ends = [item.source, item.target];
        return ends.some(
          (port) =>
            port.kind === 'splitter-out' &&
            port.moduleId === moduleId &&
            port.splitterIndex === index &&
            port.output === out,
        );
      });
      const occupied = !!patch;
      ports.push({
        id: `${moduleId}:spl:${index}:out:${out}`,
        printLabel: `P${index}/OUT-${out}`,
        status: occupied ? 'obsazeno' : 'volno',
        source: EMPTY,
        target: occupied ? 'obsazeno' : 'volno',
        hopRef: `splitterový modul ${moduleIndex} / pozice ${index} / OUT ${out}`,
        colorLabel: null,
        logicalFiber: null,
      });
    }
  }
  return ports;
}

function ofsmRouteLabel(
  schema: RozvadecOccupancySchema,
  moduleId: string,
  input: RozvadecOccupancyGenerateInput,
): string {
  const terms = schema.rsuOccupancy?.terminations.filter((item) => item.moduleId === moduleId && item.cableId) ?? [];
  const counts = new Map<string, number>();
  for (const term of terms) {
    const name = term.cableId!.trim();
    if (!name) continue;
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  let best = '';
  let bestN = 0;
  for (const [name, n] of counts) {
    if (n > bestN) {
      best = name;
      bestN = n;
    }
  }
  if (best) return best.startsWith('OK') ? best : `OK ${best}`;
  const incoming = input.incomingCables?.[0];
  if (incoming?.typOk) return incoming.typOk;
  if (incoming?.cableId) return incoming.cableId.startsWith('OK') ? incoming.cableId : `OK ${incoming.cableId}`;
  return '';
}

function pushRaySlot(
  positions: RozvadecOccupancyPositionSlot[],
  slot: RozvadecOccupancyPositionSlot,
): void {
  positions.push(slot);
}

function buildRayTr3Sheets(
  schema: RozvadecOccupancySchema,
  input: RozvadecOccupancyGenerateInput,
): RozvadecOccupancySheet[] {
  const occupancy = schema.rsuOccupancy;
  const modules = [...(occupancy?.modules ?? [])].sort((a, b) => a.rackStartU - b.rackStartU || a.odfOrder - b.odfOrder);
  const splitterOrdinal = new Map<string, number>();
  let splitterCount = 0;
  for (const module of modules) {
    const def = getRayOfsmModule(module.materialSap);
    if (def?.kind === 'splitter') {
      splitterCount += 1;
      splitterOrdinal.set(module.id, splitterCount);
    }
  }

  const positions: RozvadecOccupancyPositionSlot[] = [];
  if (modules.length === 0) {
    for (let slot = 1; slot <= RAY_TR3_D5_SLOT_COUNT; slot += 1) {
      pushRaySlot(positions, {
        position: slot,
        scaleLabels: [],
        empty: true,
        isCassetteStart: true,
        render: true,
        cassette: {
          cassetteId: null,
          cassetteType: null,
          printLabel: 'Volná pozice',
          startPosition: slot,
          span: 3,
          kind: 'empty',
          ports: [],
          routeLabel: '',
          heightU: 3,
        },
      });
    }
  } else {
    let occupiedBay = 0;
    let cursor = 1;
    const rackTop = 42;
    while (cursor <= rackTop) {
      const module = modules.find((item) => item.rackStartU === cursor);
      if (!module) {
        const next = modules.find((item) => item.rackStartU > cursor);
        const gapEnd = next ? next.rackStartU - 1 : rackTop;
        const heightU = gapEnd - cursor + 1;
        pushRaySlot(positions, {
          position: positions.length + 1,
          scaleLabels: [],
          empty: true,
          isCassetteStart: true,
          render: true,
          cassette: {
            cassetteId: null,
            cassetteType: null,
            printLabel: 'Volná pozice',
            startPosition: cursor,
            span: heightU,
            kind: 'empty',
            ports: [],
            routeLabel: '',
            heightU,
          },
        });
        cursor = gapEnd + 1;
        continue;
      }
      const def = getRayOfsmModule(module.materialSap);
      const jumper = isJumperModule(module.materialSap, def?.nazev ?? '');
      let kind: RozvadecOccupancyCassetteBlock['kind'] = 'OCCUPIED_OPAQUE';
      let printLabel = 'Obsazená pozice';
      let ports: RozvadecOccupancyPortCell[] = [];
      let columnCount = 0;
      let routeLabel = '';
      let scaleLabels: number[] = [];
      if (jumper) {
        kind = 'JUMPER';
        printLabel = 'Jumper storage 1U';
      } else if (def?.kind === 'patch-weld') {
        kind = 'OFSM';
        printLabel = def.d5TypeLabel;
        ports = ofsmPortsForModule(schema, module.id, module.materialSap, module.odfOrder);
        columnCount = def.connectorCapacity / RAY_OFSM_COLUMN_SIZE;
        routeLabel = ofsmRouteLabel(schema, module.id, input);
      } else if (def?.kind === 'splitter') {
        kind = 'SPLITTER_MODULE';
        printLabel = formatRaySplitterStandLabel(def.splitterCount, def.splitterRatio);
        ports = splitterModulePorts(schema, module.id, module.materialSap, splitterOrdinal.get(module.id) ?? 1);
      } else {
        occupiedBay += 1;
        scaleLabels = [occupiedBay];
      }
      pushRaySlot(positions, {
        position: positions.length + 1,
        scaleLabels,
        empty: false,
        isCassetteStart: true,
        render: true,
        cassette: {
          cassetteId: module.id,
          cassetteType: module.materialSap,
          printLabel,
          startPosition: module.rackStartU,
          span: module.heightU,
          kind,
          ports,
          routeLabel,
          columnCount,
          heightU: module.heightU,
        },
      });
      cursor = module.rackStartU + module.heightU;
    }
  }

  return [
    {
      id: 'ray:stand',
      kind: 'ray-stand',
      title: `${schema.printName} · RAY-TR3`,
      rowId: 'stand',
      rowLabel: (input.stojanLabel ?? schema.printName ?? 'stojan').trim() || 'stojan',
      sideLabel: null,
      frontView: true,
      pageFormat: 'A3-portrait',
      sapCode: schema.sapCode ?? EMPTY,
      configuration: 'RAY-TR3',
      networkRole: schema.networkRole,
      locality: schema.locality ?? EMPTY,
      cabinetTypeLabel: 'RAY-TR3',
      printName: schema.printName,
      objectName: schema.objectName ?? schema.printName,
      statusLabel: 'přední pohled',
      positions,
      doorSplitters: [],
      references: positions.flatMap((slot) => (slot.cassette?.ports ?? []).map((port) => port.hopRef).filter((item): item is string => !!item)),
      notes: [
        'RAY-TR3 D5 podle CETIN stojanu (Libochovany): 27 sloupců, slučované bloky, A3 nastojato.',
        'OFSM 144: 12 svislých bloků po 2 sloupcích, nahoře 1,13,25,…,133; pod každým portem 3 řádky (č. ODF / vlákno / cíl).',
        'OFSM 96/48 jen skutečné sloupce — zbylé porty neexistují. Neosazené porty vloženého OFSM = rezerva.',
        '3U obsazená/volná = 16 řádků, 1U jumper = 5, splitter pole = 6. Stavy: Obsazená pozice ≠ Volná ≠ rezerva ≠ neexistující port.',
      ],
      sheetIndex: 1,
      sheetCount: 1,
    },
  ];
}

export function buildRozvadecOccupancySheets(
  schema: RozvadecOccupancySchema,
  input: RozvadecOccupancyGenerateInput = {},
): RozvadecOccupancySheet[] {
  if (schema.rendererId === 'D5-MUK') return buildMukSheets(schema, input);
  if (schema.rendererId === 'D5-RAYTR3') return buildRayTr3Sheets(schema, input);
  void input;
  return [];
}

export function occupancySheetPortLine(ports: readonly RozvadecOccupancyPortCell[], field: 'printLabel' | 'target' | 'hopRef'): string {
  return ports
    .map((port) => {
      if (field === 'hopRef') return port.hopRef ?? '';
      return port[field];
    })
    .filter((item) => item && item !== EMPTY)
    .join('  ') || EMPTY;
}

export function occupancySheetPositionPorts(
  slot: RozvadecOccupancyPositionSlot,
): { labels: RozvadecOccupancyPortCell[]; targets: RozvadecOccupancyPortCell[] } {
  const cassette = slot.cassette;
  if (!cassette || slot.empty) return { labels: [], targets: [] };
  if (cassette.kind === 'KM5_SPLITTER') {
    const outs = cassette.ports.filter((item) => item.printLabel.startsWith('OUT'));
    const rest = cassette.ports.filter((item) => !item.printLabel.startsWith('OUT'));
    const odd = outs.filter((_, index) => index % 2 === 0);
    const even = outs.filter((_, index) => index % 2 === 1);
    return { labels: [...rest, ...odd], targets: even };
  }
  if (cassette.kind === 'KM4' || cassette.kind === 'KM6') {
    return {
      labels: cassette.ports.slice(0, 12),
      targets: cassette.ports.slice(12, 24),
    };
  }
  if (cassette.kind === 'KM5_SC' || cassette.kind === 'KM5_LC') {
    return { labels: cassette.ports, targets: cassette.ports };
  }
  return { labels: cassette.ports, targets: cassette.ports };
}

